import type { GrammarError, AIComment } from '$lib/types/grammar';
import type { AIClient } from '$lib/types/ai';
import { GrammarErrorFactory } from '../../GrammarErrorFactory';
import { AIUtilityService } from './AIUtilityService';

/**
 * AI Text Analysis Service
 * Handles full text analysis and AI response parsing for grammar errors
 * Single Responsibility: Complete text analysis and error extraction
 */
export class AITextAnalysisService {
  /**
   * Analyze entire text for grammar errors using AI
   */
  static async analyzeText(
    text: string,
    aiClient: AIClient,
    useStreaming: boolean = false
  ): Promise<{ errors: GrammarError[], comments: Map<string, AIComment> }> {
    // Create prompt for full text analysis
    const prompt = this.createFullTextAnalysisPrompt(text);

    let response: string;
    
    if (useStreaming && aiClient.supportsStreaming?.()) {
      // For now, use non-streaming as fallback
      // TODO: Implement actual streaming when client supports it
      response = await aiClient.sendMessage(prompt);
    } else {
      response = await aiClient.sendMessage(prompt);
    }

    // Parse AI response and convert to errors
    return this.parseTextAnalysisResponse(response, text, aiClient);
  }

  /**
   * Create prompt for full text analysis
   */
  private static createFullTextAnalysisPrompt(text: string): string {
    // Check if text seems to be a fragment (very short or doesn't end with punctuation)
    const isFragment = text.length < 10 || (!text.endsWith('.') && !text.endsWith('!') && !text.endsWith('?') && !text.endsWith('다') && !text.endsWith('요'));
    
    const contextNote = isFragment 
      ? '\nNOTE: This may be a text fragment or partial selection. Analyze it as-is, but be aware it might not be a complete sentence.\n' 
      : '';
    
    return `You are a strict Korean grammar and spelling checker. Analyze the following Korean text and identify ALL grammar, spelling, spacing, and style errors. Be thorough and strict.
${contextNote}
Text to analyze:
"${text}"

Check for these common Korean errors:
1. 띄어쓰기 오류 (예: "8 가지" → "8가지", "할 수 있다" → "할 수 있다")
2. 맞춤법 오류 (예: "됬다" → "됐다", "어떻해" → "어떻게")
3. 숫자 표기 (예: "이십사 평" → "24평", "8 가지" → "8가지")
4. 조사 오류 (예: "너는" → "너는", "그것 을" → "그것을")
5. 어미 오류 (예: "갔읍니다" → "갔습니다")
6. 외래어 표기 (예: "커피숍" → "커피숍", "네비게이션" → "내비게이션")
7. 문장부호 오류 (예: 쉼표, 마침표 누락 또는 잘못된 사용)
8. 불필요한 공백 (예: "능력 들" → "능력들")

For each error found, provide:
1. The EXACT original text that has an error (only the specific error part)
2. The corrected version (MUST be different from original)
3. The position (approximate character index) where the error occurs
4. An explanation in Korean
5. Confidence level (0-1)
6. Error type

Rules:
- MUST find errors even in seemingly correct text - be very strict
- Include spacing errors (띄어쓰기)
- Only include the exact error text in "original", not surrounding context
- The "correction" must be different from "original"
- Keep the error scope minimal - ideally 1-3 words only
- Focus on the specific word or short phrase that needs correction
- If unsure, still report it with lower confidence
- Check EVERY word and spacing carefully

Respond ONLY with valid JSON:
{
  "errors": [
    {
      "original": "incorrect text",
      "correction": "corrected text",
      "position": number,
      "explanation": "Korean explanation",
      "confidence": number,
      "type": "spelling" | "grammar" | "punctuation" | "spacing"
    }
  ]
}`;
  }

  /**
   * Parse AI response and convert to GrammarError format
   */
  private static parseTextAnalysisResponse(
    response: string, 
    originalText: string, 
    aiClient: AIClient
  ): { errors: GrammarError[], comments: Map<string, AIComment> } {
    let parsedResponse;
    
    // Handle empty or very short responses
    if (!response || response.trim().length < 5) {
      console.warn('AI returned empty or very short response for text analysis');
      return { errors: [], comments: new Map() };
    }
    
    try {
      const cleanResponse = AIUtilityService.cleanJsonResponse(response);
      parsedResponse = JSON.parse(cleanResponse);
    } catch (e) {
      console.warn('Initial JSON parse failed, attempting repair. Response:', response.substring(0, 200));
      // Try harder to extract JSON from response
      parsedResponse = this.extractAndRepairJson(response);
    }

    const errors: GrammarError[] = [];
    const comments = new Map<string, AIComment>();

    // Convert AI findings to GrammarError format
    if (parsedResponse.errors && Array.isArray(parsedResponse.errors)) {
      // Filter out errors where original and correction are the same
      const validErrors = parsedResponse.errors.filter((error: any) => {
        return error.original !== error.correction;
      });
      
      validErrors.forEach((aiError: any, index: number) => {
        const error = GrammarErrorFactory.fromAIAnalysisError(aiError, index, originalText);
        
        if (error.position.start !== -1) {
          // Create AI comment and attach directly to error
          const comment: AIComment = {
            text: aiError.explanation || 'AI 분석 결과',
            confidence: aiError.confidence || 0.8,
            agreesWithCorrection: true,
            alternatives: [],
            isUncertain: aiError.confidence < 0.6,
            timestamp: Date.now(),
            model: aiClient.getModelName() || 'unknown'
          };
          
          // Create a new error object with aiComment attached
          const errorWithComment = {
            ...error,
            aiComment: comment
          };
          
          errors.push(errorWithComment);
          
          // Also store in comments map for backward compatibility
          comments.set(error.id, comment);
        }
      });
    }

    return { errors, comments };
  }

  /**
   * Extract and repair JSON from AI response when parsing fails
   */
  private static extractAndRepairJson(response: string): any {
    // Handle empty or invalid response
    if (!response || response.trim().length === 0) {
      console.warn('Empty AI response, returning empty errors');
      return { errors: [] };
    }
    
    // Try to extract JSON object from response
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        let jsonStr = jsonMatch[0];
        
        // Check for malformed JSON that starts incorrectly
        if (jsonStr.trim().startsWith('{,') || jsonStr.trim().startsWith('{\n,')) {
          console.warn('Malformed JSON detected, attempting to fix');
          jsonStr = jsonStr.replace(/^\{\s*,/, '{');
        }
        
        // First, try to fix common JSON errors
        // Fix missing "original" key (when just a value is provided)
        jsonStr = jsonStr.replace(/\{\s*"([^"]+)"\s*,\s*"correction"/g, '{ "original": "$1", "correction"');
        
        // Handle truncated JSON by attempting to close it properly
        if (!jsonStr.endsWith('}')) {
          console.warn('JSON appears truncated, attempting to repair...');
          
          // Find the last complete error object by looking for complete objects
          const errorsArrayMatch = jsonStr.match(/"errors"\s*:\s*\[([\s\S]*)/);
          if (errorsArrayMatch) {
            const arrayContent = errorsArrayMatch[1];
            
            // Find all complete error objects within the array
            const errorMatches = arrayContent.match(/\{[^{}]*"original"[^{}]*"correction"[^{}]*"explanation"[^{}]*\}/g);
            
            if (errorMatches && errorMatches.length > 0) {
              // Rebuild JSON with only complete error objects
              const completeErrors = errorMatches.map(match => {
                try {
                  return JSON.parse(match);
                } catch {
                  return null;
                }
              }).filter(e => e !== null);
              
              jsonStr = JSON.stringify({ errors: completeErrors });
            } else {
              // No complete error objects found in array, fallback
              jsonStr = JSON.stringify({ errors: [] });
            }
          } else {
            // Fallback: try to close the JSON structure
            jsonStr = this.repairTruncatedJson(jsonStr);
          }
        }
        
        // Final attempt to parse
        const parsed = JSON.parse(jsonStr);
        return parsed;
      } catch (e2) {
        console.error('Failed to parse extracted JSON:', e2);
        console.error('Response was:', response.substring(0, 200)); // Log first 200 chars for debugging
        return { errors: [] };
      }
    } else {
      console.warn('No JSON structure found in AI response');
      return { errors: [] };
    }
  }

  /**
   * Repair truncated JSON string
   */
  private static repairTruncatedJson(jsonStr: string): string {
    // Remove any incomplete last property
    const lastCommaIndex = jsonStr.lastIndexOf(',');
    const lastOpenBrace = jsonStr.lastIndexOf('{');
    
    if (lastCommaIndex > lastOpenBrace) {
      // Check if there's an incomplete property after the last comma
      const afterComma = jsonStr.substring(lastCommaIndex + 1).trim();
      if (!afterComma.includes('}')) {
        // Remove the incomplete property
        jsonStr = jsonStr.substring(0, lastCommaIndex);
      }
    }
    
    // Close any open strings
    const openQuotes = (jsonStr.match(/"/g) || []).length;
    if (openQuotes % 2 !== 0) {
      jsonStr += '"';
    }
    
    // Close any open objects/arrays
    const openBrackets = (jsonStr.match(/\[/g) || []).length;
    const closeBrackets = (jsonStr.match(/\]/g) || []).length;
    const openBraces = (jsonStr.match(/\{/g) || []).length;
    const closeBraces = (jsonStr.match(/\}/g) || []).length;
    
    // Add missing brackets/braces
    for (let i = closeBrackets; i < openBrackets; i++) {
      jsonStr += ']';
    }
    for (let i = closeBraces; i < openBraces; i++) {
      jsonStr += '}';
    }
    
    return jsonStr;
  }

  /**
   * Find text position using utility service
   */
  static findTextPosition(text: string, searchText: string, approximatePosition: number): { start: number, end: number } {
    return AIUtilityService.findTextPosition(text, searchText, approximatePosition);
  }
}